// System settings. Admin only, per Section 7.5.
//
// Two groups: the rates and defaults the rest of the app reads, and the
// email settings, folded into one collapsible section that holds the
// onsite dispatch wording beside the Mailgun connection it leaves through.
// The email section opens by itself when Mailgun needs attention.

import { useEffect, useState } from 'react'
import { getSettings, saveSettings, sendMailTest } from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { Button, Detail, Input, Notice, Panel } from '../components/ui/index.js'
import FeedbackSection from '../feedback/FeedbackSection.jsx'

export default function Settings() {
  const [form, setForm] = useState(null)
  const [mail, setMail] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const [savedRates, setSavedRates] = useState(false)
  const [savedEmail, setSavedEmail] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testOutcome, setTestOutcome] = useState(null)
  const rates = useApiForm()
  const email = useApiForm()

  useEffect(() => {
    getSettings()
      .then((data) => {
        setForm(data.settings)
        setMail(data.mail)
        setEmailOpen(!data.mail?.configured || Boolean(data.mail?.regionNote))
      })
      .catch((caught) => setLoadError(caught.message))
  }, [])

  function change(field, value) {
    setSavedRates(false)
    setSavedEmail(false)
    setForm((current) => ({ ...current, [field]: value }))
  }

  // Each form saves only its own fields, so an unfinished edit in one never
  // rides along with a save in the other.
  async function onSaveRates(event) {
    event.preventDefault()
    const result = await rates.submit(() =>
      saveSettings({
        mileageRate: form.mileageRate,
        defaultSplitFranchiseePct: form.defaultSplitFranchiseePct,
      }),
    )
    if (result) {
      setForm((current) => ({
        ...current,
        mileageRate: result.settings.mileageRate,
        defaultSplitFranchiseePct: result.settings.defaultSplitFranchiseePct,
      }))
      setSavedRates(true)
    }
  }

  async function onSaveEmail(event) {
    event.preventDefault()
    const result = await email.submit(() =>
      saveSettings({
        dispatchEmailSubject: form.dispatchEmailSubject,
        dispatchEmailBody: form.dispatchEmailBody,
      }),
    )
    if (result) {
      setForm((current) => ({
        ...current,
        dispatchEmailSubject: result.settings.dispatchEmailSubject,
        dispatchEmailBody: result.settings.dispatchEmailBody,
        dispatchEmailIsDefault: result.settings.dispatchEmailIsDefault,
      }))
      setSavedEmail(true)
    }
  }

  async function onTestMail() {
    setTesting(true)
    setTestOutcome(null)
    try {
      const result = await sendMailTest()
      setTestOutcome(result.outcome)
      if (result.mail) setMail(result.mail)
    } catch (caught) {
      setTestOutcome({ sent: false, detail: caught.message, hint: null, note: null, status: null })
    } finally {
      setTesting(false)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!form) return <div className="center-note">Loading</div>

  const mailNeedsAttention = mail && (!mail.configured || mail.regionNote)

  return (
    <>
      <div className="stack">
        <div className="split">
          <Panel title="Rates and defaults">
            <form onSubmit={onSaveRates} noValidate>
              {rates.error ? <Notice tone="bad">{rates.error}</Notice> : null}
              {savedRates && !rates.error ? <Notice tone="ok">Rates saved.</Notice> : null}

              <div className="formgrid">
                <Input
                  id="mileageRate"
                  label="Mileage rate"
                  type="number"
                  step="0.001"
                  min="0"
                  value={form.mileageRate}
                  onChange={(event) => change('mileageRate', event.target.value)}
                  error={rates.fieldErrors.mileageRate}
                  help="Dollars per mile. The onsite mileage helper multiplies miles driven by this."
                />

                <Input
                  id="defaultSplitFranchiseePct"
                  label="Default franchisee share"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.defaultSplitFranchiseePct}
                  onChange={(event) => change('defaultSplitFranchiseePct', event.target.value)}
                  error={rates.fieldErrors.defaultSplitFranchiseePct}
                  help="Percentage kept by the franchisee. New customers start here, and each customer can be set individually."
                />
              </div>

              <div className="btnrow">
                <span className="spacer" />
                <Button type="submit" variant="primary" disabled={rates.saving}>
                  {rates.saving ? 'Saving' : 'Save rates'}
                </Button>
              </div>
            </form>
          </Panel>

          <div className="stack">
            <Panel title="In effect now">
              <div className="rows">
                <div className="row">
                  <div className="row-main">
                    <div className="row-primary">Mileage rate</div>
                    <div className="row-meta">Dollars per mile</div>
                  </div>
                  <div className="row-trail tabular">{form.mileageRate}</div>
                </div>
                <div className="row">
                  <div className="row-main">
                    <div className="row-primary">Franchisee share</div>
                    <div className="row-meta">Program keeps the remainder</div>
                  </div>
                  <div className="row-trail tabular">{form.defaultSplitFranchiseePct}%</div>
                </div>
              </div>
            </Panel>

            <Panel title="History does not move">
              <p className="card-note muted">
                Changing either value affects future records only. Activity records keep the split
                they were recorded with, so closed months never shift, and the mileage rate is shown
                on an onsite for reference, never stored on it.
              </p>
            </Panel>
          </div>
        </div>

        <Panel
          title="Email settings"
          support={
            mailNeedsAttention
              ? 'The onsite dispatch email and the Mailgun connection. Mailgun needs attention.'
              : 'The onsite dispatch email and the Mailgun connection it leaves through.'
          }
          open={emailOpen}
          onToggle={setEmailOpen}
        >
          <div className="split">
            <section className="subsection">
              <h3>Onsite dispatch email</h3>
              <p className="support">
                Sent to the clinic when an onsite is dispatched to it: the on-site contact email, or
                the main contact email when that is blank. The customer&apos;s protocol rides along
                through {'{protocol}'}.
              </p>

              <form onSubmit={onSaveEmail} noValidate>
                {email.error ? <Notice tone="bad">{email.error}</Notice> : null}
                {savedEmail && !email.error ? (
                  <Notice tone="ok">Email wording saved.</Notice>
                ) : null}

                <div className="formgrid">
                  <Input
                    id="dispatchEmailSubject"
                    className="span-2"
                    label="Subject"
                    value={form.dispatchEmailSubject}
                    onChange={(event) => change('dispatchEmailSubject', event.target.value)}
                    error={email.fieldErrors.dispatchEmailSubject}
                  />
                  <Input
                    id="dispatchEmailBody"
                    className="span-2"
                    label="Body"
                    multiline
                    rows={16}
                    value={form.dispatchEmailBody}
                    onChange={(event) => change('dispatchEmailBody', event.target.value)}
                    error={email.fieldErrors.dispatchEmailBody}
                    help={
                      form.dispatchEmailIsDefault
                        ? 'This is the standard wording. Edit freely; clearing both boxes restores it.'
                        : 'Custom wording. Clearing both boxes restores the standard wording.'
                    }
                  />
                </div>

                {mail?.placeholders ? (
                  <div className="rows">
                    {mail.placeholders.map(([token, meaning]) => (
                      <div key={token} className="row">
                        <div className="row-main">
                          <div className="row-primary">
                            <code>{token}</code>
                          </div>
                          <div className="row-meta">{meaning}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="btnrow">
                  <span className="spacer" />
                  <Button type="submit" variant="primary" disabled={email.saving}>
                    {email.saving ? 'Saving' : 'Save email wording'}
                  </Button>
                </div>
              </form>
            </section>

            <section className="subsection">
              <h3>Mailgun</h3>
              <p className="support">
                How dispatch emails leave the server. Compare these with the domain in Mailgun, then
                send yourself a test.
              </p>

              {mail ? (
                <>
                  {!mail.configured ? (
                    <Notice tone="warn">
                      Mailgun is not configured, so dispatch emails are not going out. Set
                      MAILGUN_API_KEY and MAILGUN_DOMAIN in the server environment.
                    </Notice>
                  ) : null}

                  <Detail label="Status">
                    {mail.configured ? 'Configured' : 'Not configured'}
                  </Detail>
                  <Detail label="Transport" wraps>
                    {mail.transport} on port 443. No SMTP.
                  </Detail>
                  <Detail label="Sending domain">{mail.domain}</Detail>
                  <Detail label="API key" wraps>
                    {mail.keySet ? `Set: ${mail.keyHint}` : null}
                  </Detail>
                  <Detail label="Region" wraps>
                    {mail.regionInUse && mail.regionInUse !== mail.region
                      ? `${mail.region.toUpperCase()} configured, ${mail.regionInUse.toUpperCase()} in use`
                      : mail.region.toUpperCase()}
                  </Detail>
                  <Detail label="API host" wraps>
                    {mail.apiHost}
                  </Detail>
                  <Detail label="From" wraps>
                    {mail.from}
                  </Detail>

                  {mail.regionNote ? <Notice tone="warn">{mail.regionNote}</Notice> : null}

                  {testOutcome?.sent ? (
                    <Notice tone="ok">
                      Mailgun accepted a test message for {testOutcome.to}. Check that inbox, and
                      its spam folder, in the next minute or two.
                      {testOutcome.note ? <p className="card-note">{testOutcome.note}</p> : null}
                    </Notice>
                  ) : null}
                  {testOutcome && !testOutcome.sent ? (
                    <Notice tone="bad">
                      <strong>{testOutcome.detail}</strong>
                      {testOutcome.hint ? <p className="card-note">{testOutcome.hint}</p> : null}
                      {testOutcome.note ? <p className="card-note">{testOutcome.note}</p> : null}
                    </Notice>
                  ) : null}

                  <div className="btnrow">
                    <span className="spacer" />
                    <Button onClick={onTestMail} disabled={testing || !mail.configured}>
                      {testing ? 'Sending' : 'Send a test email to me'}
                    </Button>
                  </div>
                </>
              ) : null}
            </section>
          </div>
        </Panel>
      </div>

      {/* Removable feedback widget; see DECISIONS.md. */}
      <FeedbackSection />
    </>
  )
}
